import { useEffect, type ReactNode } from 'react';
import { create } from 'zustand';
import { Button } from './Button';

/**
 * The prototype calls window.confirm(). A browser dialog is not in the
 * design language, so this is the same contract (a question, one
 * destructive or primary action) drawn on the bundle's menu surface: white,
 * radius 10, 1px #e3e3e1, the menu shadow, title 15/600, body 13 #6b6b70,
 * outlined Cancel + the action (destructive = red text, per PRODUCT_RULES).
 *
 * NOT in the bundle; flagged for the designer in IMPLEMENTATION_PLAN §2.2.
 *
 * Usage: `if (!(await confirm({ title, body, action: 'Delete', danger: true }))) return;`
 */
export interface ConfirmOptions {
  title: string;
  /** Usually a sentence; a node where part of it must stand out. */
  body?: ReactNode;
  action: string;
  danger?: boolean;
}

interface ConfirmState {
  open: ConfirmOptions | null;
  resolve: ((ok: boolean) => void) | null;
  ask: (o: ConfirmOptions) => Promise<boolean>;
  answer: (ok: boolean) => void;
}

export const useConfirm = create<ConfirmState>((set, get) => ({
  open: null,
  resolve: null,
  ask: (o) =>
    new Promise<boolean>((resolve) => {
      get().resolve?.(false);
      set({ open: o, resolve });
    }),
  answer: (ok) => {
    get().resolve?.(ok);
    set({ open: null, resolve: null });
  },
}));

export function confirm(o: ConfirmOptions): Promise<boolean> {
  return useConfirm.getState().ask(o);
}

export function ConfirmHost() {
  const { open, answer } = useConfirm();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') answer(false);
      if (e.key === 'Enter') answer(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, answer]);
  if (!open) return null;
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        answer(false);
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(15,15,16,.32)', display: 'grid', placeItems: 'center' }}
    >
      <div
        role="dialog"
        aria-modal
        onClick={(e) => e.stopPropagation()}
        style={{ width: 400, maxWidth: 'calc(100vw - 32px)', padding: '20px 20px 16px', background: 'var(--tr-surface)', color: 'var(--tr-ink)', border: '1px solid var(--tr-line-2)', borderRadius: 10, boxShadow: 'var(--tr-shadow-menu)', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em' }}>{open.title}</span>
          {open.body ? <div style={{ fontSize: 13, color: 'var(--tr-muted)', lineHeight: 1.5 }}>{open.body}</div> : null}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="outlined" size={32} onClick={() => answer(false)}>
            Cancel
          </Button>
          {open.danger ? (
            <Button variant="destructive" size={32} onClick={() => answer(true)}>
              {open.action}
            </Button>
          ) : (
            <Button variant="primary" size={32} onClick={() => answer(true)}>
              {open.action}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
